import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NavigationEnd, NavigationError, NavigationStart, Router, RouterModule, Routes } from '@angular/router';

import { AppComponent } from './app.component';
import { ApiAuthorizationModule } from 'src/api-authorization/api-authorization.module';
import { AuthorizeGuard } from 'src/api-authorization/authorize.guard';
import { AuthorizeInterceptor } from 'src/api-authorization/authorize.interceptor';
import { ComponentsModule } from './shared/components/components.module'
import {
  DxSchedulerModule, DxButtonModule, DxButtonGroupModule, DxPopupModule, DxDataGridModule, DxMenuModule, DxFormModule,
  DxListModule, DxTabsModule, DxToastModule, DxBulletModule, DxDrawerModule, DxLookupModule, 
  DxDateBoxModule, DxGalleryModule, DxPopoverModule, DxSwitchModule,
  DxTagBoxModule, DxToolbarModule, DxTextBoxModule, DxTooltipModule, DxCalendarModule, DxCheckBoxModule, DxColorBoxModule, DxTabPanelModule,
  DxTextAreaModule, DxTemplateModule, DxSortableModule, DxTileViewModule, DxMultiViewModule, DxDropDownButtonModule, DxScrollViewModule, DxNumberBoxComponent, DxNumberBoxModule
} from 'devextreme-angular';

import { DefaultDataService } from './services/default-data.service';
import { SignalRService } from './services/signalr.service';

import { SelectivePreloadingStrategyService } from './services/selective-preloading-strategy.service';
import { FuseMainModule } from './main/main.module';
import { FuseSharedModule } from 'src/@fuse/shared.module';
import { FuseModule } from 'src/@fuse/fuse.module';
import { fuseConfig } from './fuse-config';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppMonitoringService } from './services/app-monitoring.service';
import { DirectivesModule } from './directives/directives.module';

import { CommonModule } from '@angular/common';
import { MessageModule } from './message/message.module';
import { MessageService } from './message/message.service';
import { LoginDashboardComponent } from './login/login-dashboard.component'
import { AvatarModule, AvatarSource } from 'ngx-avatars';
import { ConstantsService } from './services/constants.service';
import { AutoLogoutService } from './services/auto-logout.service';
import { AS365PipesModule } from './shared/pipes/pipes';
import { sendRequestFactory } from 'src/api-authorization/devex-http-helper';

import devextremeAjax from 'devextreme/core/utils/ajax';
import { ErrorHandlerService } from './services/app-monitoring.service';
import { DxReportViewerModule } from 'devexpress-reporting-angular';
import { EventService } from './services/event.service';
import { HWCService } from './services/hwc.service';
import { SessionContextService } from './services/session-context.service';
import { ResourceManagerService } from './services/resource.manager.service';
import { NavigationService } from './services/navigation.service';
import { HelpComponent } from './Help/Help.component';
import { Subscription } from 'rxjs';
import { WorldPayStompService } from './services/worldpay/worldpay-stomp.service';
import { WorldPayStompServiceFactory } from './services/worldpay/worldpay-stomp-service-factory';
import { CanDeactivateGuard } from './shared/guards/can-deactivate.guard';

const appRoutes: Routes = [
  {
    path: 'frontoffice', loadChildren: () => import('./front-office/front-office.module').then(m => m.FrontOfficeModule),
    canActivate: [AuthorizeGuard]
  },

  {
    path: 'reportoffice', loadChildren: () => import('./report-office/report-office.module').then(m => m.ReportOfficeModule),
    canActivate: [AuthorizeGuard],
    data: { auth: 'ADMIN', noPreload: "true" }
  },

  {
    path: 'marketingoffice', loadChildren: () => import('./marketing-office/marketing-office.module').then(m => m.MarketingOfficeModule),
    canActivate: [AuthorizeGuard],
    data: { auth: 'ADMIN', noPreload: "true" }
  },

  {
    path: 'backoffice', loadChildren: () => import('./back-office/back-office.module').then(m => m.BackOfficeModule),
    canActivate: [AuthorizeGuard],
    data: { auth: 'ADMIN', noPreload: "true" }
  },

  { path: 'checkin', component: LoginDashboardComponent, canActivate: [AuthorizeGuard] },
  { path: 'checkin/:UID', component: LoginDashboardComponent, canActivate: [AuthorizeGuard] },
  { path: 'help', component: HelpComponent },
];

const avatarSourcesOrder = [AvatarSource.INITIALS];
const avatarColors = ["#FFB6C1", "#2c3e50", "#95a5a6", "#f39c12", "#1abc9c"];
@NgModule({
  declarations: [
    AppComponent,
    HelpComponent,
    LoginDashboardComponent,
  ],
  exports: [
    MessageModule,
    CommonModule,
    ComponentsModule,
  ],
  imports: [
    AvatarModule.forRoot({
      sourcePriorityOrder: avatarSourcesOrder,
      colors: avatarColors
    }),
    CommonModule,
    ComponentsModule,
    BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
    BrowserAnimationsModule,
    HttpClientModule,
    FormsModule,
    ApiAuthorizationModule,
    RouterModule.forRoot(appRoutes, { preloadingStrategy: SelectivePreloadingStrategyService, enableTracing: false }),
    FuseModule.forRoot(fuseConfig),
    FuseSharedModule,
    FuseMainModule,

    DirectivesModule,

    DxButtonModule, DxButtonGroupModule, DxPopupModule, DxMenuModule, DxFormModule,
    DxListModule, DxTabsModule, DxToastModule, DxBulletModule, DxDrawerModule, DxLookupModule, 
    DxDateBoxModule, DxGalleryModule, DxPopoverModule, DxScrollViewModule, DxTagBoxModule, DxToolbarModule, DxTextBoxModule,
    DxTooltipModule, DxCalendarModule, DxCheckBoxModule, DxColorBoxModule, DxTabPanelModule, DxTextAreaModule, DxTemplateModule,
    DxSortableModule, DxTileViewModule, DxMultiViewModule, DxDropDownButtonModule, DxNumberBoxModule, DxSwitchModule,
    AS365PipesModule,

    DxReportViewerModule,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: AuthorizeInterceptor, multi: true },
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    EventService,
    AppMonitoringService,
    ConstantsService,
    SessionContextService,
    AutoLogoutService,
    MessageService,
    HWCService,
    SignalRService,
    DefaultDataService,
    ResourceManagerService,
    NavigationService,
    CanDeactivateGuard,
    { provide: WorldPayStompService, useFactory: WorldPayStompServiceFactory}
  ],
  bootstrap: [AppComponent]
})
export class AppModule implements OnDestroy {
  router$: Subscription;

  // constructor() {
  // }

  // This code will allow devextreme data source classes to use Angular httpClient. This means we can use the same http interceptor
  // to inject auth bearer token into the http request header, and check the result for 403 and redirect to login
  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private defaultData: DefaultDataService,
    private hwc: HWCService,
    private signalR: SignalRService,
    private rm: ResourceManagerService,    
  ) {
    console.log("App Module Starting");
    devextremeAjax.inject({ sendRequest: sendRequestFactory(httpClient) });

    // get default data
    // defaultData.GetDefaultData();

    this.router$ = router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        console.log("Router Navigation Start " + event);
        // Show loading indicator
      }
      if (event instanceof NavigationEnd) {
        // Hide loading indicator
        console.log("Router Navigation End " + event);
      }
      if (event instanceof NavigationError) {
        // Hide loading indicator
        console.log("Router Navigation Error " + event);
        // Present error to user
        console.log(event.error);
      }
    });
  }

  ngOnDestroy(): void {
    this.router$?.unsubscribe();
  }
}
